import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef, GridApi, ICellRendererParams, RowDoubleClickedEvent } from '@ag-grid-community/core';
import { useQuery } from 'convex/react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  FileSpreadsheet,
  Filter,
  Loader2,
  RotateCcw,
  Shield,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import type { ChipOption } from '@/components/chip-group';
import { ChipGroup } from '@/components/chip-group';
import Container from '@/components/container';
import { CopyCountButton } from '@/components/copy-count-button';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useGrid } from '@/contexts/grid-context';
import { cn } from '@/lib/utils';
import { api } from '../../../../convex/_generated/api';

// ============================================================================
// Types & facets
// ============================================================================

type ZoneStatus = 'active' | 'dropped';
type Tld = 'bank' | 'insurance';
type Dnssec = 'signed' | 'unsigned';

interface ZoneDomainRow {
  domain: string;
  kind: Tld;
  status: ZoneStatus;
  firstSeen: number;
  lastSeen: number;
  ns_count: number;
  a_count: number;
  aaaa_count: number;
  ds_count: number;
  nameservers: string[];
}

const STATUS_CONFIG: Record<ZoneStatus, { label: string; className: string; icon: React.ElementType; description: string }> = {
  active: {
    label: 'In zone',
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
    icon: CheckCircle2,
    description: 'Present in the most recent successful zone import',
  },
  dropped: {
    label: 'Dropped',
    className: 'bg-slate-200 text-slate-700 hover:bg-slate-200',
    icon: XCircle,
    description: 'Was in the zone at some point but absent from the latest import; kept for history',
  },
};

const TLD_CONFIG: Record<Tld, { label: string; className: string }> = {
  bank: { label: '.Bank', className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' },
  insurance: { label: '.Insurance', className: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-100' },
};

const DNSSEC_CONFIG: Record<Dnssec, { label: string; className: string }> = {
  signed: { label: 'DNSSEC signed', className: 'bg-sky-100 text-sky-800 hover:bg-sky-100' },
  unsigned: { label: 'No DS', className: 'bg-amber-100 text-amber-800 hover:bg-amber-100' },
};

const STATUS_OPTIONS: ChipOption<ZoneStatus>[] = (['active', 'dropped'] as const).map((s) => ({
  value: s,
  label: STATUS_CONFIG[s].label,
  icon: STATUS_CONFIG[s].icon,
  className: STATUS_CONFIG[s].className,
  title: STATUS_CONFIG[s].description,
}));

const TLD_OPTIONS: ChipOption<Tld>[] = (['bank', 'insurance'] as const).map((t) => ({
  value: t,
  label: TLD_CONFIG[t].label,
  className: TLD_CONFIG[t].className,
}));

const DNSSEC_OPTIONS: ChipOption<Dnssec>[] = (['signed', 'unsigned'] as const).map((d) => ({
  value: d,
  label: DNSSEC_CONFIG[d].label,
  className: DNSSEC_CONFIG[d].className,
}));

function toggleIn<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

// ============================================================================
// Stat cards (current zone only)
// ============================================================================

function ZoneStatCard({ title, icon: Icon, stats }: {
  title: string;
  icon: React.ElementType;
  stats: { domains: number; dropped: number; ns: number; ds: number; dnssec: number };
}) {
  const dnssecPct = stats.domains > 0 ? Math.round((stats.dnssec / stats.domains) * 100) : 0;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-3">
          <div className="text-2xl font-bold">{stats.domains.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">domains in zone</p>
          {stats.dropped > 0 && (
            <span className="text-xs text-muted-foreground">
              (+
              {stats.dropped.toLocaleString()}
              {' '}
              dropped, kept for history)
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div>
            <div className="text-sm font-medium">{stats.ns.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">NS records</p>
          </div>
          <div>
            <div className="text-sm font-medium">{stats.ds.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">DS records</p>
          </div>
          <div>
            <div className="text-sm font-medium">
              {stats.dnssec.toLocaleString()}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                (
                {dnssecPct}
                %)
              </span>
            </div>
            <p className="text-xs text-muted-foreground">DNSSEC signed</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Page
// ============================================================================

function ZonesPage() {
  const [selectedStatuses, setSelectedStatuses] = useState<ZoneStatus[]>(['active']);
  const [selectedTlds, setSelectedTlds] = useState<Tld[]>([]);
  const [selectedDnssec, setSelectedDnssec] = useState<Dnssec[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const gridApiRef = useRef<GridApi<ZoneDomainRow> | null>(null);
  const { registerGridApi } = useGrid();
  const navigate = useNavigate();

  const zoneData = useQuery(api.czds.getZonePageData, {});
  const isLoading = zoneData === undefined;

  const allRows = useMemo((): ZoneDomainRow[] => {
    if (!zoneData?.domains)
      return [];
    return zoneData.domains.map((d) => ({
      domain: d.domain,
      kind: d.kind,
      status: d.isActive ? 'active' : 'dropped',
      firstSeen: d.firstSeen,
      lastSeen: d.lastSeen,
      ns_count: d.ns_count,
      a_count: d.a_count,
      aaaa_count: d.aaaa_count,
      ds_count: d.ds_count,
      nameservers: d.nameservers,
    }));
  }, [zoneData]);

  const dnssecOf = (r: ZoneDomainRow): Dnssec => (r.ds_count > 0 ? 'signed' : 'unsigned');

  const filteredRows = useMemo(() => allRows.filter((r) => {
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(r.status))
      return false;
    if (selectedTlds.length > 0 && !selectedTlds.includes(r.kind))
      return false;
    if (selectedDnssec.length > 0 && !selectedDnssec.includes(dnssecOf(r)))
      return false;
    return true;
  }), [allRows, selectedStatuses, selectedTlds, selectedDnssec]);

  // Chip counts: each facet's counts respect the OTHER facets, so the numbers
  // always describe what clicking the chip would show.
  const countBy = useCallback((skip: 'status' | 'tld' | 'dnssec', key: (r: ZoneDomainRow) => string) => {
    const counts: Record<string, number> = {};
    for (const r of allRows) {
      if (skip !== 'status' && selectedStatuses.length > 0 && !selectedStatuses.includes(r.status))
        continue;
      if (skip !== 'tld' && selectedTlds.length > 0 && !selectedTlds.includes(r.kind))
        continue;
      if (skip !== 'dnssec' && selectedDnssec.length > 0 && !selectedDnssec.includes(dnssecOf(r)))
        continue;
      const k = key(r);
      counts[k] = (counts[k] ?? 0) + 1;
    }
    return counts;
  }, [allRows, selectedStatuses, selectedTlds, selectedDnssec]);

  const statusCounts = useMemo(() => countBy('status', (r) => r.status), [countBy]);
  const tldCounts = useMemo(() => countBy('tld', (r) => r.kind), [countBy]);
  const dnssecCounts = useMemo(() => countBy('dnssec', dnssecOf), [countBy]);

  const hasActiveFilters
    = !(selectedStatuses.length === 1 && selectedStatuses[0] === 'active')
      || selectedTlds.length > 0
      || selectedDnssec.length > 0
      || searchQuery.trim() !== '';

  const clearFilters = useCallback(() => {
    setSelectedStatuses(['active']);
    setSelectedTlds([]);
    setSelectedDnssec([]);
    setSearchQuery('');
    gridApiRef.current?.setFilterModel(null);
  }, []);

  const handleExport = useCallback(() => {
    if (filteredRows.length === 0) {
      toast.error('No domains to export');
      return;
    }
    try {
      const exportRows = filteredRows.map((r) => ({
        'Domain': r.domain,
        'TLD': TLD_CONFIG[r.kind].label,
        'Status': STATUS_CONFIG[r.status].label,
        'First Seen': format(new Date(r.firstSeen), 'MM/dd/yyyy'),
        'Last Seen': format(new Date(r.lastSeen), 'MM/dd/yyyy'),
        'NS Records': r.ns_count,
        'DS Records': r.ds_count,
        'A Glue': r.a_count,
        'AAAA Glue': r.aaaa_count,
        'Nameservers': r.nameservers.join(', '),
      }));
      const wb = utils.book_new();
      const ws = utils.json_to_sheet(exportRows);
      ws['!cols'] = [
        { wch: 30 },
        { wch: 11 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
        { wch: 11 },
        { wch: 11 },
        { wch: 9 },
        { wch: 10 },
        { wch: 60 },
      ];
      utils.book_append_sheet(wb, ws, 'Zone');
      const filename = `zone_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
      writeFile(wb, filename);
      toast.success(`Exported ${exportRows.length} domains to ${filename}`);
    } catch {
      toast.error('Failed to export');
    }
  }, [filteredRows]);

  const columnDefs = useMemo((): ColDef<ZoneDomainRow>[] => [
    { field: 'domain', headerName: 'Domain', minWidth: 220, flex: 2, filter: true },
    {
      field: 'kind',
      headerName: 'TLD',
      minWidth: 110,
      filter: true,
      valueFormatter: (p) => TLD_CONFIG[p.value as Tld]?.label ?? p.value,
      cellRenderer: (p: ICellRendererParams<ZoneDomainRow>) => (
        <Badge className={TLD_CONFIG[p.value as Tld].className}>{TLD_CONFIG[p.value as Tld].label}</Badge>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      filter: true,
      valueFormatter: (p) => STATUS_CONFIG[p.value as ZoneStatus]?.label ?? p.value,
      cellRenderer: (p: ICellRendererParams<ZoneDomainRow>) => {
        const cfg = STATUS_CONFIG[p.value as ZoneStatus];
        const Icon = cfg.icon;
        return (
          <Badge className={cn('gap-1', cfg.className)} title={cfg.description}>
            <Icon className="size-3" />
            {cfg.label}
          </Badge>
        );
      },
    },
    {
      field: 'lastSeen',
      headerName: 'Last seen in zone',
      minWidth: 170,
      sort: 'desc',
      valueFormatter: (p) => (p.value ? format(new Date(p.value), 'yyyy-MM-dd') : '—'),
      tooltipValueGetter: (p) => (p.value ? formatDistanceToNow(new Date(p.value), { addSuffix: true }) : ''),
    },
    {
      field: 'firstSeen',
      headerName: 'First seen',
      minWidth: 130,
      valueFormatter: (p) => (p.value ? format(new Date(p.value), 'yyyy-MM-dd') : '—'),
    },
    {
      field: 'ds_count',
      headerName: 'DNSSEC',
      minWidth: 120,
      filter: 'agNumberColumnFilter',
      cellRenderer: (p: ICellRendererParams<ZoneDomainRow>) => {
        const signed = (p.value ?? 0) > 0;
        return (
          <Badge className={signed ? DNSSEC_CONFIG.signed.className : DNSSEC_CONFIG.unsigned.className}>
            {signed ? `${p.value} DS` : 'No DS'}
          </Badge>
        );
      },
    },
    { field: 'ns_count', headerName: 'NS', minWidth: 80, filter: 'agNumberColumnFilter' },
    { field: 'a_count', headerName: 'A glue', minWidth: 90, filter: 'agNumberColumnFilter' },
    { field: 'aaaa_count', headerName: 'AAAA glue', minWidth: 105, filter: 'agNumberColumnFilter' },
    {
      field: 'nameservers',
      headerName: 'Nameservers',
      minWidth: 300,
      flex: 3,
      filter: true,
      valueFormatter: (p) => (Array.isArray(p.value) ? p.value.join(', ') : ''),
      tooltipValueGetter: (p) => (Array.isArray(p.value) ? p.value.join('\n') : ''),
    },
  ], []);

  const getRowId = useCallback((p: { data: ZoneDomainRow }) => p.data.domain, []);

  const handleRowDoubleClick = useCallback((event: RowDoubleClickedEvent<ZoneDomainRow>) => {
    if (event.data)
      navigate(`/app/domains/${event.data.domain}#dns`);
  }, [navigate]);

  const handleGridReady = useCallback((params: { api: GridApi<ZoneDomainRow> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  }, [registerGridApi]);

  // Import health: the daily CZDS import can fail; if it has, say so instead
  // of silently showing a days-old zone as current.
  const importStale = zoneData?.lastImport?.completedAt
    ? Date.now() - zoneData.lastImport.completedAt > 36 * 60 * 60 * 1000
    : false;
  const latestFailed = zoneData?.latestAttempt?.status === 'failed'
    && zoneData.latestAttempt.date !== zoneData.lastImport?.date;

  return (
    <Container className="pb-12">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Zone Files</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isLoading || filteredRows.length === 0}
          className="gap-2"
        >
          <FileSpreadsheet className="size-4" />
          Export
          {' '}
          {filteredRows.length.toLocaleString()}
          {' '}
          domains
        </Button>
      </div>

      {zoneData?.lastImport && (
        <div className={cn('mb-4 flex flex-wrap items-center gap-2 text-sm', importStale || latestFailed ? 'text-amber-700' : 'text-muted-foreground')}>
          {(importStale || latestFailed) && <AlertTriangle className="size-4" />}
          <span>
            Zone as of
            {' '}
            <span className="font-medium">{zoneData.lastImport.date}</span>
            {zoneData.lastImport.completedAt && (
              <>
                {' '}
                (
                {formatDistanceToNow(new Date(zoneData.lastImport.completedAt), { addSuffix: true })}
                )
              </>
            )}
            {' · '}
            {zoneData.lastImport.totalDomains.toLocaleString()}
            {' '}
            names in that import
          </span>
          {latestFailed && zoneData.latestAttempt && (
            <span title={zoneData.latestAttempt.error ?? undefined}>
              · Latest import attempt (
              {zoneData.latestAttempt.date}
              ) failed
              {zoneData.latestAttempt.error ? `: ${zoneData.latestAttempt.error.trim()}` : ''}
            </span>
          )}
        </div>
      )}

      {isLoading && (
        <Card className="mb-6">
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span>Loading zone data...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && zoneData && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <ZoneStatCard title=".Bank Zone" icon={Building2} stats={zoneData.stats.bank} />
            <ZoneStatCard title=".Insurance Zone" icon={Shield} stats={zoneData.stats.insurance} />
          </div>

          <Card className="mb-6 border-dashed">
            <CardContent className="py-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-x-8 gap-y-4">
                  <ChipGroup
                    label="Status"
                    options={STATUS_OPTIONS}
                    selected={selectedStatuses}
                    onToggle={(s) => setSelectedStatuses((prev) => toggleIn(prev, s))}
                    counts={statusCounts}
                    all={{
                      onClick: () => setSelectedStatuses([]),
                      count: (statusCounts.active ?? 0) + (statusCounts.dropped ?? 0),
                      title: 'Every domain the importer has ever seen',
                    }}
                  />
                  <ChipGroup
                    label="TLD"
                    options={TLD_OPTIONS}
                    selected={selectedTlds}
                    onToggle={(t) => setSelectedTlds((prev) => toggleIn(prev, t))}
                    counts={tldCounts}
                  />
                  <ChipGroup
                    label="DNSSEC"
                    options={DNSSEC_OPTIONS}
                    selected={selectedDnssec}
                    onToggle={(d) => setSelectedDnssec((prev) => toggleIn(prev, d))}
                    counts={dnssecCounts}
                  />
                </div>

                {/* Controls bar */}
                <div className="flex items-center justify-between gap-3 border-t pt-3">
                  <div className="flex items-center gap-2">
                    {hasActiveFilters
                      ? (
                          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1.5 text-xs text-muted-foreground">
                            <RotateCcw className="size-3" />
                            Reset to current zone
                          </Button>
                        )
                      : (
                          <span className="text-xs text-muted-foreground">
                            Showing the current zone. Click a chip to filter, or add Dropped to see names that have left.
                          </span>
                        )}
                  </div>

                  <div className="flex items-center gap-3">
                    <CopyCountButton count={filteredRows.length} label="domains" />

                    <div className="relative w-56">
                      <Input
                        type="search"
                        placeholder="Search domains or nameservers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-8 pl-8 text-xs"
                      />
                      <Filter className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {selectedStatuses.includes('dropped') && (
            <Card className="mb-4 border-slate-300 bg-slate-50">
              <CardContent className="py-3">
                <p className="text-sm text-slate-700">
                  <strong>Dropped domains</strong>
                  {' '}
                  were in the zone at some point but were absent from the latest import. Their record counts and
                  nameservers are as of the last import that contained them, not the current zone. They are not monitored.
                </p>
              </CardContent>
            </Card>
          )}

          <AGDataGrid
            columnDefs={columnDefs}
            rowData={filteredRows}
            quickFilterText={searchQuery}
            getRowId={getRowId}
            onRowDoubleClicked={handleRowDoubleClick}
            onGridReady={handleGridReady}
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No domains match the selected filters</span>'
          />
        </>
      )}
    </Container>
  );
}

export default ZonesPage;
